/** @file Shell of a document panel: the open document to `view`, or a note while no project is open. */
import type { IDockviewPanelProps } from 'dockview-react';
import { useEffect, type ReactNode } from 'react';
import { useStore } from 'zustand';
import type { Doc } from '@pose2d/format/types';
import type { Translate } from '@pose2d/i18n/types';
import { useT } from '../shell/i18n/useT';
import { currentDoc } from '../stores/currentDoc';
import { docStore } from '../stores/docStore';
import { installSelectionPrune } from '../stores/installSelectionPrune';

/** Props of DocumentPanel; `api` is the dock panel's (its id names the note's test id). */
export interface DocumentPanelProps {
  api: Pick<IDockviewPanelProps['api'], 'id'>;
  view: (doc: Doc, t: Translate) => ReactNode;
}

/** Re-renders with every document change (the confirmed document plus pending commands, W3-10) and language change;
 *  while mounted it keeps the selection on live entities. */
export function DocumentPanel({ api, view }: DocumentPanelProps) {
  const t = useT();
  const doc = useStore(docStore, currentDoc);
  useEffect(() => installSelectionPrune(), []);
  if (doc === null) return <p className="p2-panel-note" data-testid={`panel-${api.id}`}>{t('forms.noProject')}</p>;
  return <>{view(doc, t)}</>;
}
